import React, { useState } from 'react';
import { Button } from '../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogScrollArea,
  DialogFooter,
} from '@/components/ui/dialog';
import { 
  Copy, 
  ExternalLink, 
  CheckCircle,
  Workflow,
  Eye,
  EyeOff,
  Key,
  Database,
  Zap,
  Settings,
  Code
} from 'lucide-react';
import { BrandIcons } from '../../ui/brand-icons';
import { toast } from 'sonner';

export interface N8NSuccessIntegration {
  integration_name: string;
  binding_id: string;
  auth_config: {
    api_key: string;
  };
  space_id: string;
  permissions: any;
  rate_limits: any;
}

export interface N8NSuccessModalContentProps {
  onClose: () => void;
  integration: N8NSuccessIntegration;
}

export const N8NSuccessModalContent: React.FC<N8NSuccessModalContentProps> = ({
  onClose,
  integration,
}) => {
  const [copiedItem, setCopiedItem] = useState<string | null>(null);
  const [showApiKey, setShowApiKey] = useState(false);

  const handleCopy = async (text: string, item: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedItem(item);
      toast.success(`${item} copied to clipboard`);
      setTimeout(() => setCopiedItem(null), 2000);
    } catch (error) {
      toast.error('Failed to copy to clipboard');
    }
  };

  const npmInstallCommand = 'npm install n8n-nodes-memara';
  // Use environment variable for API base URL with fallback
  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';

  return (
    <div className="flex min-h-0 flex-1 flex-col">
        <DialogHeader className="flex-shrink-0 p-6 pb-4 pr-12">
          <DialogTitle className="text-xl font-semibold text-gray-900 flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            n8n Integration Created Successfully!
          </DialogTitle>
        </DialogHeader>

        <DialogScrollArea className="space-y-6 px-6">
          {/* Success Summary */}
          <Card className="border-green-200 bg-green-50">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-green-900">{integration.integration_name}</h3>
                  <p className="text-green-700">Integration ID: {integration.binding_id}</p>
                </div>
                <BrandIcons.n8n className="w-12 h-12" />
              </div>
            </CardContent>
          </Card>

          {/* Credentials Section */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg text-gray-900 flex items-center gap-2">
                <Key className="w-5 h-5 text-blue-600" />
                Your n8n Credentials
              </CardTitle>
              <p className="text-sm text-gray-600">
                Add these credentials to your n8n instance to connect with Memara
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* API Base URL */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  API Base URL
                </label>
                <div className="flex items-center space-x-2">
                  <div className="flex-1 bg-gray-50 border border-gray-300 rounded-md px-3 py-2 text-sm font-mono break-all overflow-x-auto">
                    {apiBaseUrl}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleCopy(apiBaseUrl, 'API Base URL')}
                    className="flex-shrink-0"
                  >
                    {copiedItem === 'API Base URL' ? (
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>

              {/* API Key */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  API Key
                </label>
                <div className="flex items-center space-x-2">
                  <div className="flex-1 bg-gray-50 border border-gray-300 rounded-md px-3 py-2 text-sm font-mono break-all overflow-x-auto">
                    {showApiKey ? integration.auth_config.api_key : '•'.repeat(32)}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowApiKey(!showApiKey)}
                    className="flex-shrink-0"
                  >
                    {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleCopy(integration.auth_config.api_key, 'API Key')}
                    className="flex-shrink-0"
                  >
                    {copiedItem === 'API Key' ? (
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Setup Instructions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg text-gray-900 flex items-center gap-2">
                <Workflow className="w-5 h-5 text-red-600" />
                Installation Methods
              </CardTitle>
              <p className="text-sm text-gray-600">
                Choose the method that matches your n8n setup
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Installation Methods */}
              <div className="grid md:grid-cols-3 gap-4">
                {/* Method 1: Verified Community Node */}
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="text-center mb-3">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    </div>
                    <h4 className="font-semibold text-green-900 text-sm">Verified Node</h4>
                    <Badge variant="secondary" className="bg-green-100 text-green-800 text-xs mt-1">
                      Easiest
                    </Badge>
                  </div>
                  <div className="space-y-1 text-xs text-green-800">
                    <p>• Open nodes panel (+ or Tab)</p>
                    <p>• Search "Memara"</p>
                    <p>• Click Install</p>
                  </div>
                </div>

                {/* Method 2: GUI Installation */}
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="text-center mb-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2">
                      <Settings className="w-4 h-4 text-blue-600" />
                    </div>
                    <h4 className="font-semibold text-blue-900 text-sm">GUI Install</h4>
                    <Badge variant="secondary" className="bg-blue-100 text-blue-800 text-xs mt-1">
                      Self-Hosted
                    </Badge>
                  </div>
                  <div className="space-y-1 text-xs text-blue-800">
                    <p>• Settings → Community Nodes</p>
                    <p>• Click Install</p>
                    <p>• Enter: n8n-nodes-memara</p>
                  </div>
                </div>

                {/* Method 3: Manual Installation */}
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <div className="text-center mb-3">
                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-2">
                      <Code className="w-4 h-4 text-purple-600" />
                    </div>
                    <h4 className="font-semibold text-purple-900 text-sm">Manual</h4>
                    <Badge variant="secondary" className="bg-purple-100 text-purple-800 text-xs mt-1">
                      Advanced
                    </Badge>
                  </div>
                  <div className="space-y-2">
                    <div className="bg-gray-900 text-white p-1 rounded font-mono text-xs">
                      docker exec -it n8n sh
                    </div>
                    <div className="bg-gray-900 text-white p-1 rounded font-mono text-xs">
                      npm i n8n-nodes-memara
                    </div>
                  </div>
                </div>
              </div>

              {/* Common Setup Steps */}
              <div className="space-y-4">
                <div className="text-center">
                  <h4 className="font-semibold text-gray-900 text-sm mb-2">After Installation</h4>
                  <p className="text-xs text-gray-600">Complete these steps for any installation method</p>
                </div>

                {/* Step 1 */}
                <div className="flex items-start space-x-4 p-4 bg-red-50 rounded-lg border border-red-200">
                  <div className="flex-shrink-0 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                    1
                  </div>
                  <div className="flex-grow">
                    <h4 className="font-semibold text-red-900 mb-2">Choose Installation Method</h4>
                    <p className="text-red-800 text-sm mb-3">
                      Use one of the three methods above based on your n8n deployment
                    </p>
                    <div className="flex items-center space-x-2">
                      <div className="bg-gray-900 text-white px-3 py-2 rounded font-mono text-sm flex-1">
                        {npmInstallCommand}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleCopy(npmInstallCommand, 'Install Command')}
                        className="flex-shrink-0"
                      >
                        {copiedItem === 'Install Command' ? (
                          <CheckCircle className="w-4 h-4 text-green-600" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Step 2 */}
                <div className="flex items-start space-x-4 p-4 bg-orange-50 rounded-lg border border-orange-200">
                  <div className="flex-shrink-0 w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                    2
                  </div>
                  <div className="flex-grow">
                    <h4 className="font-semibold text-orange-900 mb-2">Configure Credentials</h4>
                    <p className="text-orange-800 text-sm mb-2">
                      Add your Memara credentials in n8n's credential settings:
                    </p>
                    <ul className="text-sm text-orange-800 space-y-1">
                      <li>• Go to Settings → Credentials in n8n</li>
                      <li>• Create new "Memara API" credential</li>
                      <li>• Use the API Base URL and API Key shown above</li>
                    </ul>
                  </div>
                </div>

                {/* Step 3 */}
                <div className="flex items-start space-x-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <div className="flex-shrink-0 w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                    3
                  </div>
                  <div className="flex-grow">
                    <h4 className="font-semibold text-purple-900 mb-2">Create Your First Workflow</h4>
                    <p className="text-purple-800 text-sm mb-2">
                      Start building memory-powered automations:
                    </p>
                    <ul className="text-sm text-purple-800 space-y-1">
                      <li>• Add the Memara node you need (for example Create Memory or Search Memories) or a Memara trigger</li>
                      <li>• Select the Memara API credential</li>
                      <li>• Configure memory content and test</li>
                    </ul>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Available Operations */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg text-gray-900 flex items-center gap-2">
                <Database className="w-5 h-5 text-indigo-600" />
                Available Operations
              </CardTitle>
              <p className="text-sm text-gray-600">
                Based on the permissions you configured for this integration
              </p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {integration.permissions?.can_read && (
                  <div className="text-center p-3 bg-green-50 rounded-lg border border-green-200">
                    <Zap className="w-6 h-6 text-green-600 mx-auto mb-1" />
                    <p className="text-sm font-medium text-green-900">Read Memory</p>
                  </div>
                )}
                {integration.permissions?.can_write && (
                  <div className="text-center p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <Zap className="w-6 h-6 text-blue-600 mx-auto mb-1" />
                    <p className="text-sm font-medium text-blue-900">Create Memory</p>
                  </div>
                )}
                {integration.permissions?.can_search && (
                  <div className="text-center p-3 bg-orange-50 rounded-lg border border-orange-200">
                    <Zap className="w-6 h-6 text-orange-600 mx-auto mb-1" />
                    <p className="text-sm font-medium text-orange-900">Search Memory</p>
                  </div>
                )}
                {integration.permissions?.can_delete && (
                  <div className="text-center p-3 bg-red-50 rounded-lg border border-red-200">
                    <Zap className="w-6 h-6 text-red-600 mx-auto mb-1" />
                    <p className="text-sm font-medium text-red-900">Delete Memory</p>
                  </div>
                )}
              </div>
              {integration.permissions?.rate_limits?.requests_per_hour && (
                <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600">
                    <strong>Rate Limit:</strong> {integration.permissions.rate_limits.requests_per_hour.toLocaleString()} requests per hour
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

        </DialogScrollArea>
        <DialogFooter variant="scrollable" className="flex-col gap-3 px-6 pb-6 sm:flex-row">
            <Button
              onClick={onClose}
              className="bg-red-600 hover:bg-red-700 text-white sm:flex-1"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Complete Setup
            </Button>
            <Button
              variant="outline"
              asChild
              className="border-red-300 text-red-600 hover:bg-red-50"
            >
              <a
                href="https://www.npmjs.com/package/n8n-nodes-memara"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2"
              >
                <ExternalLink className="w-4 h-4" />
                View Community Node
              </a>
            </Button>
            <Button
              variant="outline"
              asChild
              className="border-red-300 text-red-600 hover:bg-red-50"
            >
              <a
                href="https://docs.n8n.io/integrations/community-nodes/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2"
              >
                <ExternalLink className="w-4 h-4" />
                n8n Documentation
              </a>
            </Button>
        </DialogFooter>
    </div>
  );
};

interface N8NSuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  integration: N8NSuccessIntegration;
}

export const N8NSuccessModal: React.FC<N8NSuccessModalProps> = ({
  isOpen,
  onClose,
  integration,
}) => (
  <Dialog
    open={isOpen}
    onOpenChange={(open) => {
      if (!open) onClose();
    }}
  >
    <DialogContent variant="scrollable" className="max-w-5xl bg-white">
      <N8NSuccessModalContent integration={integration} onClose={onClose} />
    </DialogContent>
  </Dialog>
);
